import { queryOptions, useSuspenseQuery } from "@tanstack/react-query";
import { Link, createFileRoute, notFound } from "@tanstack/react-router";
import { ArrowLeft } from "lucide-react";

import { BlogContent } from "@/lib/blog-content";
import { faqFromContent } from "@/lib/blog-meta";
import { getPostBySlug } from "@/lib/blog.functions";

const SITE_URL = "https://h2jcompany.com.br";

const postQuery = (slug: string) =>
  queryOptions({
    queryKey: ["blog", "post", slug],
    queryFn: () => getPostBySlug({ data: slug }),
  });

export const Route = createFileRoute("/blog_/$slug")({
  loader: async ({ context, params }) => {
    const post = await context.queryClient.ensureQueryData(postQuery(params.slug));
    if (!post) throw notFound();
    return post;
  },
  head: ({ loaderData }) => {
    if (!loaderData) {
      return {
        meta: [{ title: "Conteúdo não encontrado" }, { name: "robots", content: "noindex" }],
      };
    }
    const description = loaderData.excerpt || `Artigo da H2J Growth Company: ${loaderData.title}`;
    const cover = loaderData.cover_image_url;
    const hasAbsoluteCover = typeof cover === "string" && cover.startsWith("https://");
    const url = `${SITE_URL}/blog/${loaderData.slug}`;
    const faq = faqFromContent(loaderData.content);

    const article = {
      "@context": "https://schema.org",
      "@type": "BlogPosting",
      headline: loaderData.title,
      description,
      inLanguage: "pt-BR",
      mainEntityOfPage: url,
      url,
      ...(hasAbsoluteCover ? { image: [cover] } : {}),
      datePublished: loaderData.published_at ?? loaderData.created_at,
      dateModified: loaderData.updated_at ?? loaderData.published_at ?? loaderData.created_at,
      author: { "@type": "Organization", name: "H2J Growth Company", url: SITE_URL },
      publisher: {
        "@type": "Organization",
        name: "H2J Growth Company",
        url: SITE_URL,
        logo: { "@type": "ImageObject", url: `${SITE_URL}/h2j-logo.png` },
      },
    };
    const faqPage =
      faq.length > 0
        ? {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: faq.map((item) => ({
              "@type": "Question",
              name: item.question,
              acceptedAnswer: { "@type": "Answer", text: item.answer },
            })),
          }
        : null;

    return {
      meta: [
        { title: `${loaderData.title} | Blog H2J` },
        { name: "description", content: description },
        { property: "og:title", content: loaderData.title },
        { property: "og:description", content: description },
        { property: "og:type", content: "article" },
        { property: "og:url", content: url },
        { name: "twitter:card", content: "summary_large_image" },
        ...(loaderData.published_at
          ? [{ property: "article:published_time", content: loaderData.published_at }]
          : []),
        ...(hasAbsoluteCover
          ? [
              { property: "og:image", content: cover },
              { name: "twitter:image", content: cover },
            ]
          : []),
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [article, ...(faqPage ? [faqPage] : [])].map((data) => ({
        type: "application/ld+json",
        children: JSON.stringify(data).replace(/</g, "\\u003c"),
      })),
    };
  },
  component: BlogPostPage,
  notFoundComponent: PostNotFound,
});

function BlogPostPage() {
  const { data: post } = useSuspenseQuery(postQuery(Route.useParams().slug));
  if (!post) return <PostNotFound />;

  return (
    <main className="min-h-screen bg-background text-foreground">
      <header className="border-b border-border/70">
        <div className="section-shell flex h-18 items-center justify-between">
          <Link to="/" aria-label="H2J Growth Company">
            <img
              src="/h2j-logo-340.webp"
              alt="H2J Growth Company"
              width={340}
              height={96}
              className="h-10 w-auto"
            />
          </Link>
          <Link
            to="/blog"
            className="inline-flex items-center gap-2 text-[0.7rem] font-semibold uppercase tracking-[0.18em] text-muted-foreground transition-colors hover:text-accent-ink"
          >
            <ArrowLeft className="size-4" /> Todos os artigos
          </Link>
        </div>
      </header>

      <article className="section-shell max-w-3xl py-16 sm:py-24">
        <p className="eyebrow">Blog H2J</p>
        <h1 className="mt-4 text-3xl font-semibold leading-[1.15] sm:text-5xl">{post.title}</h1>
        {post.published_at && (
          <p className="mt-5 text-[0.7rem] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
            {new Date(post.published_at).toLocaleDateString("pt-BR", {
              day: "2-digit",
              month: "long",
              year: "numeric",
            })}
          </p>
        )}
        {post.cover_image_url && (
          <img
            src={post.cover_image_url}
            alt={post.title}
            width={1280}
            height={720}
            className="mt-10 aspect-[16/9] w-full rounded-2xl object-cover"
          />
        )}
        <div className="mt-12">
          <BlogContent content={post.content} />
        </div>

        <div className="mt-16 border-t-2 border-primary bg-surface-soft p-8">
          <h2 className="text-2xl font-semibold">Quer crescer com estratégia?</h2>
          <p className="mt-3 text-muted-foreground">
            Solicite um diagnóstico gratuito e descubra o próximo gargalo da sua empresa.
          </p>
          <Link
            to="/"
            className="mt-6 inline-flex items-center justify-center rounded-md bg-primary px-5 py-3 text-sm font-semibold text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Quero um diagnóstico
          </Link>
        </div>
      </article>
    </main>
  );
}

function PostNotFound() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-2xl font-semibold text-foreground">Artigo não encontrado</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Este conteúdo não existe ou ainda não foi publicado.
        </p>
        <Link
          to="/blog"
          className="mt-6 inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
        >
          Ver todos os artigos
        </Link>
      </div>
    </div>
  );
}
